import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import {
  financeService,
  FinanceMember,
  FinanceMemberDetail,
} from '../../services/finance.service'
import VaBalanceHistory from '../../components/finance/VaBalanceHistory'
import VaBalanceBulkUploadModal from '../../components/finance/VaBalanceBulkUploadModal'
import LoadingSpinner from '../../components/LoadingSpinner'
import { formatCurrency as formatRm } from '../../utils/formatters'

/**
 * Finance staff workspace: search a member, view their membership info,
 * update the informational VA Balance, and read its audit history.
 * Deliberately the only screen Finance can reach.
 */
function FinanceVABalancePage() {
  const { t } = useTranslation()

  const [search, setSearch] = useState('')
  const [results, setResults] = useState<FinanceMember[]>([])
  const [searching, setSearching] = useState(false)
  const [selected, setSelected] = useState<FinanceMemberDetail | null>(null)
  const [loadingDetail, setLoadingDetail] = useState(false)

  const [vabEditing, setVabEditing] = useState(false)
  const [vabValue, setVabValue] = useState('')
  const [vabNote, setVabNote] = useState('')
  const [vabSaving, setVabSaving] = useState(false)
  const [vabError, setVabError] = useState<string | null>(null)

  const [historyLoading, setHistoryLoading] = useState(false)
  const [historyTotal, setHistoryTotal] = useState(0)
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false)

  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const runSearch = useCallback(async (term: string) => {
    if (term.trim().length < 2) {
      setResults([])
      return
    }
    try {
      setSearching(true)
      const res = await financeService.searchMembers(term.trim())
      setResults(res.data)
    } catch {
      // toast handled by interceptor
    } finally {
      setSearching(false)
    }
  }, [])

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => runSearch(search), 300)
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current)
    }
  }, [search, runSearch])

  const selectMember = async (member: FinanceMember) => {
    // Close the results dropdown and clear the field — the selected member's
    // detail panel takes over as the context.
    setSearch('')
    setResults([])
    try {
      setLoadingDetail(true)
      setVabEditing(false)
      setVabError(null)
      const detail = await financeService.getMember(member.id)
      setSelected(detail)
      setHistoryTotal(detail.vaBalanceLogs.length)
    } catch {
      // toast handled by interceptor
    } finally {
      setLoadingDetail(false)
    }
  }

  const loadMoreHistory = async () => {
    if (!selected) return
    try {
      setHistoryLoading(true)
      const res = await financeService.getVaBalanceLogs(selected.id, 100)
      setSelected({ ...selected, vaBalanceLogs: res.data })
      setHistoryTotal(res.meta.total)
    } catch {
      // toast handled by interceptor
    } finally {
      setHistoryLoading(false)
    }
  }

  const startEdit = () => {
    if (!selected) return
    setVabValue(
      selected.virtualAccountBalance === null ? '' : String(selected.virtualAccountBalance),
    )
    setVabNote('')
    setVabError(null)
    setVabEditing(true)
  }

  const handleSave = async () => {
    if (!selected) return
    const trimmed = vabValue.trim()
    if (!trimmed || Number.isNaN(Number(trimmed))) {
      setVabError(t('finance.invalid', 'Enter a valid amount (max 2 decimal places)'))
      return
    }
    const balance = Number(trimmed)
    if (balance < 0) {
      setVabError(t('finance.negative', 'VA Balance cannot be negative'))
      return
    }
    if (!/^\d+(\.\d{1,2})?$/.test(trimmed)) {
      setVabError(t('finance.invalid', 'Enter a valid amount (max 2 decimal places)'))
      return
    }
    try {
      setVabSaving(true)
      setVabError(null)
      const result = await financeService.setVaBalance(
        selected.id,
        balance,
        vabNote.trim() || undefined,
      )
      if (result.unchanged) {
        toast.success(t('finance.unchanged', 'No change to save'))
      } else {
        toast.success(t('finance.saved', 'VA Balance updated'))
      }
      setVabEditing(false)
      setVabNote('')
      const detail = await financeService.getMember(selected.id)
      setSelected(detail)
      setHistoryTotal(detail.vaBalanceLogs.length)
    } catch (err: any) {
      setVabError(err?.response?.data?.message || 'Failed to save VA Balance')
    } finally {
      setVabSaving(false)
    }
  }

  const hasVaBalance = selected?.virtualAccountBalance !== null && selected?.virtualAccountBalance !== undefined

  // A bulk upload may have changed the selected member's balance — refresh it.
  const handleBulkCompleted = async () => {
    if (!selected) return
    try {
      const detail = await financeService.getMember(selected.id)
      setSelected(detail)
      setHistoryTotal(detail.vaBalanceLogs.length)
    } catch {
      // toast handled by interceptor
    }
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-3xl mx-auto px-6">
        <div className="mb-6 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('finance.title', 'VA Balance')}
            </h1>
            <p className="text-gray-600 mt-1">
              {t(
                'finance.subtitle',
                'Search a member and update their informational virtual account balance'
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setBulkUploadOpen(true)}
            className="shrink-0 rounded-md bg-[#0D7330] px-4 py-2 text-sm font-medium text-white hover:bg-[#0a5c27]"
          >
            {t('finance.bulkUpload.button', 'Bulk Upload')}
          </button>
        </div>

        <VaBalanceBulkUploadModal
          open={bulkUploadOpen}
          onClose={() => setBulkUploadOpen(false)}
          onCompleted={handleBulkCompleted}
        />

        {/* Search */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('finance.member', 'Member')}
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('finance.searchPlaceholder', 'Search by member ID, name or email')}
            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D7330]"
          />
          {searching && (
            <div className="mt-4 flex justify-center">
              <LoadingSpinner />
            </div>
          )}
          {!searching && search.trim().length >= 2 && results.length === 0 && (
            <p className="mt-4 text-sm text-gray-500 italic">
              {t('finance.noResults', 'No members found')}
            </p>
          )}
          {!searching && results.length > 0 && (
            <ul className="mt-4 divide-y divide-gray-100 border border-gray-100 rounded-lg">
              {results.map((m) => (
                <li key={m.id}>
                  <button
                    onClick={() => selectMember(m)}
                    className={`w-full text-left px-4 py-3 hover:bg-gray-50 flex items-center justify-between gap-4 ${
                      selected?.id === m.id ? 'bg-green-50' : ''
                    }`}
                  >
                    <span className="flex items-center gap-3 min-w-0">
                      <span className="font-mono text-xs bg-gray-100 px-2 py-0.5 rounded whitespace-nowrap">
                        {m.memberId}
                      </span>
                      <span className="text-sm text-gray-900 truncate">
                        {`${m.firstName} ${m.lastName}`.trim()}
                      </span>
                      <span className="text-xs text-gray-500 truncate hidden sm:inline">
                        {m.email}
                      </span>
                    </span>
                    {!m.isActive && (
                      <span className="text-xs text-red-600 whitespace-nowrap">
                        {t('finance.inactive', 'Inactive')}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {loadingDetail && (
          <div className="flex justify-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {/* Detail */}
        {!loadingDetail && selected && (
          <>
            <div className="bg-white rounded-xl shadow-md p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">
                  {t('finance.membershipInfo', 'Membership Information')}
                </h2>
                {!vabEditing && (
                  <button
                    onClick={startEdit}
                    disabled={vabSaving}
                    className="px-3 py-1.5 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 disabled:opacity-40"
                  >
                    {t('finance.edit', 'Edit')}
                  </button>
                )}
              </div>

              <dl className="space-y-3 text-sm mb-5">
                <Row label={t('finance.userId', 'User ID')}>
                  <span className="font-mono">{selected.memberId}</span>
                </Row>
                <Row label={t('finance.name', 'Name')}>
                  {`${selected.firstName} ${selected.lastName}`.trim()}
                </Row>
                <Row label={t('finance.email', 'Email')}>{selected.email}</Row>
                <Row label={t('finance.walletId', 'Wallet ID')}>
                  {selected.walletNumber ? (
                    <span className="font-mono">{selected.walletNumber}</span>
                  ) : (
                    <span className="text-gray-400 italic">
                      {t('finance.notAssigned', 'Not assigned')}
                    </span>
                  )}
                </Row>
                <Row label={t('finance.virtualAccount', 'Virtual Account')}>
                  {selected.virtualAccountNumber ? (
                    <span className="font-mono">
                      {selected.virtualAccountBank
                        ? `${selected.virtualAccountBank} · ${selected.virtualAccountNumber}`
                        : selected.virtualAccountNumber}
                    </span>
                  ) : (
                    <span className="text-gray-400 italic">
                      {t('finance.notAssigned', 'Not assigned')}
                    </span>
                  )}
                </Row>
              </dl>

              <div className="pt-4 border-t border-gray-100">
                {hasVaBalance && !selected.virtualAccountNumber && !vabEditing && (
                  <p className="mb-3 text-sm text-amber-700">
                    {t('finance.noVaNumber', 'No bank virtual account assigned to this member.')}
                  </p>
                )}

                {vabEditing ? (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">
                        {t('finance.vaBalance', 'VA Balance')} (RM)
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={vabValue}
                        onChange={(e) => setVabValue(e.target.value)}
                        placeholder="e.g. 1250.50"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D7330]"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-500 mb-1">
                        {t('finance.note', 'Note (optional)')}
                      </label>
                      <input
                        type="text"
                        maxLength={200}
                        value={vabNote}
                        onChange={(e) => setVabNote(e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D7330]"
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      {t(
                        'finance.informationalNote',
                        'Informational only — this balance is maintained manually and is not the wallet balance.'
                      )}
                    </p>
                    {vabError && <p className="text-red-600 text-xs">{vabError}</p>}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleSave}
                        disabled={vabSaving}
                        className="px-4 py-2 text-sm font-medium text-white bg-[#0D7330] rounded-lg hover:bg-[#0a5c27] disabled:opacity-40"
                      >
                        {vabSaving ? '...' : t('finance.save', 'Save')}
                      </button>
                      <button
                        onClick={() => {
                          setVabEditing(false)
                          setVabError(null)
                        }}
                        disabled={vabSaving}
                        className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40"
                      >
                        {t('finance.cancel', 'Cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">{t('finance.vaBalance', 'VA Balance')}</p>
                      <p className="text-3xl font-bold text-gray-900 mt-1">
                        {hasVaBalance
                          ? formatRm(selected.virtualAccountBalance!)
                          : (
                            <span className="text-gray-400 italic text-base font-normal">
                              {t('finance.notSet', 'Not set')}
                            </span>
                          )}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Audit history */}
            <div className="bg-white rounded-xl shadow-md p-6">
              <VaBalanceHistory logs={selected.vaBalanceLogs} />
              {selected.vaBalanceLogs.length < historyTotal && (
                <button
                  onClick={loadMoreHistory}
                  disabled={historyLoading}
                  className="mt-4 text-sm text-[#0D7330] hover:underline disabled:opacity-40"
                >
                  {historyLoading
                    ? '...'
                    : t('finance.loadMore', 'Load full history')}
                </button>
              )}
            </div>
          </>
        )}

        {!loadingDetail && !selected && (
          <div className="bg-white rounded-xl shadow-md p-10 text-center">
            <p className="text-sm text-gray-500">
              {t('finance.selectMember', 'Select a member to view and edit their VA balance')}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-gray-900 font-medium text-right">{children}</dd>
    </div>
  )
}

export default FinanceVABalancePage
